// Account Picker Tree - Hierarchical account selector using local IndexedDB data
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Check,
  ChevronsUpDown,
  ChevronRight,
  ChevronDown,
  Loader2,
  Search,
  Library,
  Folder,
  FolderOpen,
  FileText,
  AlertCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { TreeNode } from '@/app/pages/accounting/chart-of-accounts/components/treeTypes';
import { useAccountPickerTree, AccountPickerTreeFilters } from './useAccountPickerTree';

export interface AccountPickerTreeProps extends AccountPickerTreeFilters {
  value: number | null | undefined;
  onChange: (value: number | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export const AccountPickerTree: React.FC<AccountPickerTreeProps> = ({
  value,
  onChange,
  placeholder = 'Select account...',
  disabled = false,
  className,
  filterAccountNature,
  filterAccountTypeIds,
  filterAccountGroupIds,
  filterAccountIds,
  showInactiveAccounts = false,
}) => {
  const [open, setOpen] = useState(false);
  const [focusedNodeId, setFocusedNodeId] = useState<string | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const {
    visibleNodes,
    isLoadingInitial,
    error,
    expandedIds,
    toggleExpand,
    expandToNode,
    searchQuery,
    setSearchQuery,
    selectedAccount,
    needsSync,
    isSyncing,
    syncData,
  } = useAccountPickerTree({
    selectedAccountId: value,
    filterAccountNature,
    filterAccountTypeIds,
    filterAccountGroupIds,
    filterAccountIds,
    showInactiveAccounts,
  });

  // Focus search input when popover opens
  useEffect(() => {
    if (open) {
      setTimeout(() => searchInputRef.current?.focus(), 50);

      // Expand to show selected account if there is one
      if (value && selectedAccount) {
        const selectedNodeId = `account-${value}`;
        expandToNode(selectedNodeId);
        setFocusedNodeId(selectedNodeId);
      }
    } else {
      setSearchQuery('');
      setFocusedNodeId(null);
    }
  }, [open, value, selectedAccount, expandToNode, setSearchQuery]);

  // Get display label
  const displayLabel = useMemo(() => {
    if (value === null || value === undefined) return placeholder;
    if (selectedAccount) {
      const code = selectedAccount.code ? ` (${selectedAccount.code})` : '';
      return `${selectedAccount.name}${code}`;
    }
    return placeholder;
  }, [value, selectedAccount, placeholder]);

  // Handle node click
  const handleNodeClick = useCallback(
    (node: TreeNode) => {
      if (node.nodeType === 'account') {
        // Select the account
        onChange(node.rawId);
        setOpen(false);
        triggerRef.current?.focus();
      } else {
        // Toggle expand/collapse for types and groups
        toggleExpand(node.id);
      }
    },
    [onChange, toggleExpand]
  );

  // Handle expand toggle (for chevron clicks)
  const handleExpandToggle = useCallback(
    (e: React.MouseEvent, node: TreeNode) => {
      e.stopPropagation();
      toggleExpand(node.id);
    },
    [toggleExpand]
  );

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const nodes = visibleNodes.filter(n => {
        // Skip inactive accounts if not showing them
        if (!showInactiveAccounts && n.nodeType === 'account' && n.isActive === false) {
          return false;
        }
        return true;
      });

      if (nodes.length === 0) return;

      const currentIndex = focusedNodeId
        ? nodes.findIndex(n => n.id === focusedNodeId)
        : -1;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (currentIndex < nodes.length - 1) {
            setFocusedNodeId(nodes[currentIndex + 1].id);
          } else if (currentIndex === -1 && nodes.length > 0) {
            setFocusedNodeId(nodes[0].id);
          }
          break;

        case 'ArrowUp':
          e.preventDefault();
          if (currentIndex > 0) {
            setFocusedNodeId(nodes[currentIndex - 1].id);
          }
          break;

        case 'ArrowRight':
          e.preventDefault();
          if (focusedNodeId) {
            const node = nodes.find(n => n.id === focusedNodeId);
            if (node && node.hasChildren && !expandedIds.has(node.id)) {
              toggleExpand(node.id);
            }
          }
          break;

        case 'ArrowLeft':
          e.preventDefault();
          if (focusedNodeId) {
            const node = nodes.find(n => n.id === focusedNodeId);
            if (node) {
              if (expandedIds.has(node.id)) {
                toggleExpand(node.id);
              } else if (node.parentNodeId) {
                setFocusedNodeId(node.parentNodeId);
              }
            }
          }
          break;

        case 'Enter':
          e.preventDefault();
          if (focusedNodeId) {
            const node = nodes.find(n => n.id === focusedNodeId);
            if (node) {
              handleNodeClick(node);
            }
          }
          break;

        case 'Escape':
          e.preventDefault();
          setOpen(false);
          triggerRef.current?.focus();
          break;
      }
    },
    [
      visibleNodes,
      focusedNodeId,
      expandedIds,
      toggleExpand,
      handleNodeClick,
      showInactiveAccounts,
    ]
  );

  // Scroll focused node into view
  useEffect(() => {
    if (focusedNodeId && listRef.current) {
      const element = listRef.current.querySelector(
        `[data-node-id="${focusedNodeId}"]`
      );
      if (element) {
        element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [focusedNodeId]);

  // Render node icon
  const renderNodeIcon = (node: TreeNode) => {
    if (node.nodeType === 'account_type') {
      return (
        <FileText
          className={cn(
            'h-4 w-4 flex-shrink-0',
            node.nature === 'debit' ? 'text-blue-500' : 'text-green-500'
          )}
        />
      );
    }

    if (node.nodeType === 'account_group') {
      const isExpanded = expandedIds.has(node.id);
      return isExpanded ? (
        <FolderOpen className="h-4 w-4 flex-shrink-0 text-amber-500" />
      ) : (
        <Folder className="h-4 w-4 flex-shrink-0 text-amber-500" />
      );
    }

    return <Library className="h-4 w-4 flex-shrink-0 text-muted-foreground" />;
  };

  // Render expand/collapse chevron
  const renderChevron = (node: TreeNode) => {
    if (!node.hasChildren) {
      return <span className="w-4" />;
    }

    const isExpanded = expandedIds.has(node.id);
    return (
      <button
        type="button"
        onClick={(e) => handleExpandToggle(e, node)}
        className="p-0.5 hover:bg-accent rounded"
      >
        {isExpanded ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronRight className="h-3 w-3" />
        )}
      </button>
    );
  };

  // Render nature badge for account types
  const renderNatureBadge = (node: TreeNode) => {
    if (node.nodeType !== 'account_type' || !node.nature) return null;

    return (
      <span
        className={cn(
          'ml-auto text-xs px-1.5 py-0.5 rounded',
          node.nature === 'debit'
            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
            : 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
        )}
      >
        {node.nature === 'debit' ? 'Dr' : 'Cr'}
      </span>
    );
  };

  // Render single tree node
  const renderNode = (node: TreeNode) => {
    const isSelected = node.nodeType === 'account' && node.rawId === value;
    const isFocused = node.id === focusedNodeId;
    const isInactive = node.isActive === false;

    return (
      <div
        key={node.id}
        data-node-id={node.id}
        onClick={() => handleNodeClick(node)}
        className={cn(
          'flex items-center gap-1 px-2 py-1.5 cursor-pointer select-none rounded-sm text-sm',
          isFocused && 'bg-accent',
          isSelected && 'bg-primary/10 font-medium',
          isInactive && 'opacity-50',
          !isFocused && !isSelected && 'hover:bg-accent/50'
        )}
        style={{ paddingLeft: `${8 + node.level * 16}px` }}
        role="treeitem"
        aria-selected={isSelected}
        aria-expanded={node.hasChildren ? expandedIds.has(node.id) : undefined}
      >
        {renderChevron(node)}
        {renderNodeIcon(node)}
        <span className="truncate flex-1">{node.name}</span>
        {node.code && (
          <span className="text-xs text-muted-foreground ml-1">
            {node.code}
          </span>
        )}
        {renderNatureBadge(node)}
        {isSelected && <Check className="h-4 w-4 text-primary ml-1" />}
      </div>
    );
  };

  // Render sync prompt
  const renderSyncPrompt = () => (
    <Alert className="m-2">
      <AlertCircle className="h-4 w-4" />
      <AlertDescription className="space-y-2">
        <p>No accounts found in local database. Sync data to enable account selection.</p>
        <Button
          onClick={() => {
            console.log('[AccountPickerTree] Sync button clicked');
            syncData();
          }}
          disabled={isSyncing}
          size="sm"
          className="w-full"
        >
          {isSyncing ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Syncing...
            </>
          ) : (
            'Sync Now'
          )}
        </Button>
      </AlertDescription>
    </Alert>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn('w-full justify-between font-normal', className)}
          disabled={disabled || (isLoadingInitial && !needsSync) || !!error}
        >
          {isLoadingInitial && !needsSync ? (
            <span className="flex items-center">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading...
            </span>
          ) : error ? (
            <span className="text-destructive">Error loading accounts</span>
          ) : needsSync ? (
            <span className="text-muted-foreground">Click to sync accounts...</span>
          ) : (
            <span className="truncate">{displayLabel}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0"
        align="start"
      >
        <div
          className="flex flex-col"
          onKeyDown={handleKeyDown}
        >
          {/* Search input */}
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <Input
              ref={searchInputRef}
              placeholder="Search accounts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="border-0 focus-visible:ring-0 h-10 px-0"
              disabled={needsSync}
            />
          </div>

          {/* Tree list */}
          <ScrollArea className="h-[300px]">
            <div
              ref={listRef}
              role="tree"
              aria-label="Account tree"
              className="py-1"
            >
              {/* Sync prompt when no local data */}
              {needsSync && renderSyncPrompt()}

              {/* Loading state */}
              {isLoadingInitial && !needsSync && (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
                  Loading accounts...
                </div>
              )}

              {/* Error state */}
              {error && !isLoadingInitial && (
                <div className="py-6 text-center text-sm text-destructive">
                  Error loading accounts.
                </div>
              )}

              {/* Empty state (after data loaded) */}
              {!isLoadingInitial && !error && !needsSync && visibleNodes.length === 0 && (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  {searchQuery ? 'No accounts found.' : 'No accounts available.'}
                </div>
              )}

              {/* Tree nodes */}
              {!isLoadingInitial && !error && !needsSync &&
                visibleNodes.map((node) => renderNode(node))}
            </div>
          </ScrollArea>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default AccountPickerTree;
